/**
 * Spinner — small accessible loading indicator.
 *
 * Spec: PORTAL_MIGRATION_FROM_PSM_SSOT.md §4.1.2 (Phase 1 foundation).
 * Uses a CSS animation injected once at the module level. No external
 * dependency. The animation name is namespaced (`cp-spinner-rotate`) so
 * it does not collide with other libraries.
 */
import { useEffect } from 'react';

const KEYFRAME_ID = 'cp-spinner-keyframe';
const KEYFRAME_CSS =
  '@keyframes cp-spinner-rotate { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }';

function ensureKeyframes(): void {
  if (typeof document === 'undefined') return;
  if (document.getElementById(KEYFRAME_ID)) return;
  const style = document.createElement('style');
  style.id = KEYFRAME_ID;
  style.appendChild(document.createTextNode(KEYFRAME_CSS));
  document.head.appendChild(style);
}

interface SpinnerProps {
  /** Visual diameter in pixels. Default: 20. */
  size?: number;
  /** Accessible label. Defaults to "Loading". */
  label?: string;
  /** When true, center the spinner inside a block-level wrapper with
   *  generous padding. Useful for empty-state replacement during fetch. */
  block?: boolean;
}

export function Spinner({
  size = 20,
  label = 'Loading',
  block = false,
}: SpinnerProps): JSX.Element {
  useEffect(() => {
    ensureKeyframes();
  }, []);
  const ring = (
    <span
      role="status"
      aria-live="polite"
      aria-label={label}
      style={{
        display: 'inline-block',
        width: size,
        height: size,
        border: `${Math.max(2, Math.round(size / 10))}px solid #e5e7eb`,
        borderTopColor: '#1f4ed8',
        borderRadius: '50%',
        animation: 'cp-spinner-rotate 0.8s linear infinite',
      }}
    />
  );
  if (block) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '2rem',
        }}
      >
        {ring}
      </div>
    );
  }
  return ring;
}
